/**
 * Listener achievements (ENG-362), served by `GET /me/achievements` (ENG-361,
 * ENG-370).
 *
 * The backend is the authority on what a badge is and when it unlocks; the
 * client only displays. Until ENG-370 the badges were scored here from the
 * listener's own `/user/requests` and `/user/tips` payloads; that catalogue
 * (tips, played requests, distinct performers, active days) is gone, and the
 * one the backend serves (qualified listens, current follows, submitted
 * requests) replaces it. A badge that is not in the response does not exist.
 *
 * Endpoint (authenticated, under `config.apiBaseUrl`):
 *   GET /me/achievements
 *     → { userId, asOf, catalogVersion,
 *         status: 'ready' | 'processing' | 'unavailable',
 *         sources: { [metric]: status }, progress: { [metric]: number },
 *         categories: [{ key, title }],
 *         achievements: [{ key, version, metric, category, icon, title,
 *                          description, unit, threshold, progress, state,
 *                          unlocked, unlockedAt?, hidden? }] }
 *     (catalogVersion 7, ENG-824: `categories` and each entry's `category`
 *     are the display groups; `unit` is `count` or `cents`, US cents for the
 *     money metrics `request.paid`, `boost` and `tip`.)
 *     `achievements` is the whole catalogue in catalogue order, always
 *     present. `progress` is the metric's current value, not capped at the
 *     threshold, and for a follow badge can be below it while `unlocked`
 *     (unlocks are permanent; follows are a state). `unlockedAt` is a UTC
 *     instant, omitted when locked. `hidden` marks a badge the listener is
 *     not told about until they earn it (see below; the one field here not
 *     reconciled with the shipped backend). `state` is `locked`
 *     (no progress), `in_progress`, `unlocked`, or `unavailable` (the
 *     metric's source is not enabled on this deployment, so progress cannot
 *     be earned there; an earlier unlock still shows as `unlocked`). Overall
 *     `status` is `processing` when a source's projection could not be
 *     repaired on this read (the served progress may lag), else
 *     `unavailable` when a source is not enabled, else `ready`.
 *     401 → fetchWrapper ends the session; 503 → no database; 500 → read
 *     failure; 404/501 → the backend does not serve the endpoint yet.
 *
 * Hidden badges (ENG-411) [unreconciled]: a badge whose entry carries
 * `hidden: true` is a surprise the listener is meant to discover, so while it
 * is locked nothing of it is rendered — not its copy, not its progress, not a
 * placeholder card, and not the "of N badges" denominator, which would give
 * away that there is something to find. `normalizeAchievementsResponse` drops
 * those entries (`isRevealedAchievement`), so every list below, the seen set
 * and the DOM only ever hold badges the listener may see, and the page
 * withholds the "collected every badge" copy while anything was dropped
 * (`withheldCount`), since that claim would not be true. Once the backend
 * reports it `unlocked` the entry is served like any other and renders as a
 * full card, `hidden` still set so the page can mark it as the secret it was.
 * The flag is only read as hiding when it is exactly `true` (`hidden` or
 * `is_hidden`, the casing tolerance `unlockedAt` already has), so an entry
 * without it is an ordinary badge.
 *
 * This is a client-side hide, not secrecy: a locked hidden entry the backend
 * chooses to serve still arrives in the response, so its key, copy, threshold
 * and progress are in the browser (the Network tab, any copy of the payload)
 * whatever the page shows. Only the backend withholding the entry keeps it
 * from the listener; ENG-411 does not require that, and this holds either way.
 *
 * `hidden` is the frontend's expectation of the field and has not been diffed
 * against the shipped backend: `_helpers/__fixtures__/meAchievements.json`, the
 * captured response, carries no hidden entry, and the fixture is only ever
 * refreshed from the backend, never edited to match this file — so the tests
 * below synthesize the flag instead. Read the handler and its DTO (the
 * catalogue definitions this table is diffed against), then refresh the
 * fixture with a hidden entry and drop the `[unreconciled]` above. Until
 * then, treat a badge named or nested differently on the wire as a live
 * possibility: the flag would read `false`, every test would still pass, and
 * a secret badge would render as an ordinary locked card.
 *
 * Everything the backend sends passes through `normalizeAchievementsResponse`
 * so a shape change lands here, not in the page. Grouping follows the
 * backend's served `categories` and `category` (ENG-826), with a per-metric
 * fallback; the client adds an icon and the catalog messages of the badge's
 * copy per key, in `ACHIEVEMENT_PRESENTATION` below, falling back to the
 * served icon (when the page has it) and then the metric's, so a badge the
 * backend adds still renders before this file learns about it.
 *
 * A badge is identified everywhere by the backend's `key` (`<metric>.<name>`),
 * so the seen set below carries over from ENG-371, which re-keyed it to those
 * keys ahead of this cutover.
 *
 * Nothing here is rendered as-is: the category labels are catalog keys
 * (`labelKey`) the page resolves with `t()`, and `formatProgress` takes the
 * component's `t` (English without one) — ENG-386.
 *
 * Badge copy (ENG-381): the backend's `title` and `description` are English.
 * The page renders them in the selected locale from the stable `key`, not
 * from those words: `describeAchievement` resolves the key through
 * `ACHIEVEMENT_PRESENTATION` to the `achievements.badges.*` catalog messages
 * when the client knows the key *and* the entry's `version` (the definition
 * version the message was written for) *and* the served `threshold` is the
 * one that version's copy describes, and the active locale has the
 * translation (`t.has`). Otherwise the backend's words are shown as sent: a
 * badge the backend added, a definition it changed, or a translation the
 * locale lacks is never guessed at. The backend's own rule for when
 * `version` moves lives in its repo and is not relied on here: a threshold
 * changed under the same version (a config-only change, say) would leave the
 * catalog description contradicting the progress bar next to it, so the
 * threshold is checked as well, and an entry without a `version` matches no
 * catalog copy at all rather than being read as the first version. The
 * English catalog carries the backend's words verbatim, so English output is
 * unchanged; the helper tests hold the two together.
 */

import { englishT } from '../locales/index.js';
import { englishFormat } from './formatters.js';

export const ACHIEVEMENTS_ENDPOINT = '/me/achievements';

export const ACHIEVEMENTS_SEEN_KEY = 'listenerAchievementsSeen';

/** On a first visit (nothing stored yet) only badges unlocked this recently read as new. */
export const NEW_ACHIEVEMENT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Overall response and per-source statuses, as the backend spells them. */
export const ACHIEVEMENTS_STATUS = Object.freeze({
  READY: 'ready',
  PROCESSING: 'processing',
  UNAVAILABLE: 'unavailable',
});

/** Per-achievement states, as the backend spells them. */
export const ACHIEVEMENT_STATE = Object.freeze({
  LOCKED: 'locked',
  IN_PROGRESS: 'in_progress',
  UNLOCKED: 'unlocked',
  UNAVAILABLE: 'unavailable',
});

/**
 * The display categories this build has headings for, keyed by the backend's
 * category key (`categories[].key`, an entry's `category`) and in the
 * backend's display order. Each entry's `labelKey` is the catalog key of the
 * group heading, so a known group is headed in the selected locale rather
 * than with the backend's English `title`.
 *
 * The page groups by what the backend serves (ENG-826): the response's
 * `categories` give the groups and their order, and each entry's `category`
 * the group it sits in. This table is the fallback for a response that does
 * not carry them (`METRIC_CATEGORIES` picks the group from the metric), and
 * the heading for the keys it knows. A category the backend adds is still
 * shown, under the backend's own title; an entry whose group cannot be
 * worked out at all is grouped under `OTHER_CATEGORY` rather than dropped,
 * so the backend can add one without a frontend release.
 */
export const ACHIEVEMENT_CATEGORIES = Object.freeze({
  listening: Object.freeze({ labelKey: 'achievements.categories.listening' }),
  streaks: Object.freeze({ labelKey: 'achievements.categories.streaks' }),
  following: Object.freeze({ labelKey: 'achievements.categories.following' }),
  requests: Object.freeze({ labelKey: 'achievements.categories.requests' }),
  boosts: Object.freeze({ labelKey: 'achievements.categories.boosts' }),
  tipping: Object.freeze({ labelKey: 'achievements.categories.tipping' }),
});

export const OTHER_CATEGORY = Object.freeze({ id: 'other', labelKey: 'achievements.categories.other' });

/**
 * The category of each backend metric, for an entry that does not name its
 * own (`category`) or names one the response does not list. Mirrors the
 * backend's definitions.go: a paid request is a request, a boost has a group
 * of its own (ENG-824).
 */
const METRIC_CATEGORIES = Object.freeze({
  listen: 'listening',
  streak: 'streaks',
  follow: 'following',
  request: 'requests',
  'request.played': 'requests',
  'request.paid': 'requests',
  boost: 'boosts',
  tip: 'tipping',
});

/**
 * What an entry's `threshold` and `progress` are measured in (`unit`, ENG-824).
 * `cents` is US cents (2500 is $25.00); anything else — `count`, a unit
 * this build does not know, or none — is shown as the bare number.
 */
export const ACHIEVEMENT_UNIT = Object.freeze({ COUNT: 'count', CENTS: 'cents' });

/**
 * Every icon name the page has a glyph for: the backend's icon vocabulary
 * (`Icons` in definitions.go) plus the names this file's tables use. The
 * page's icon map must cover exactly these (its tests hold the two
 * together). An entry's served `icon` is used only when it is one of them;
 * any other name falls back to the metric's icon, then a generic trophy.
 */
export const ACHIEVEMENT_ICON_NAMES = Object.freeze([
  // The backend's vocabulary.
  'headphones',
  'radio',
  'vinyl',
  'flame',
  'calendar-week',
  'calendar-month',
  'heart',
  'star',
  'crowd',
  'music-note',
  'playlist',
  'microphone',
  'speaker',
  'applause',
  'stage',
  'coin',
  'gift',
  'gem',
  'trophy',
  'ticket',
  'spotlight',
  'crown',
  'rocket',
  'lightning',
  'megaphone',
  // Client-only names (the icons the first badges shipped with).
  'music',
  'users',
  'compass',
  'play',
]);

const KNOWN_ICONS = new Set(ACHIEVEMENT_ICON_NAMES);

/**
 * What the client adds to each badge the backend serves, keyed by backend
 * `key` (the identities the backend's definitions.go persists; diff against
 * this when the catalogue changes):
 *   icon            an icon name the page maps to a component; `METRIC_ICONS`
 *                   covers a key that is not here
 *   versions        `{ [definition version]: threshold }` — the badge
 *                   definition versions whose copy the catalog messages below
 *                   render truthfully, each with the threshold that copy
 *                   describes; any other version, or one of these served with
 *                   another threshold, shows the backend's words until this
 *                   entry learns it
 *   titleKey,       the catalog messages of the badge's title and
 *   descriptionKey  description, the English form verbatim the backend's
 * The catalog keys must exist in en-US (the catalog gate scans this table),
 * and the helper tests hold their English against the fixture, so a copy
 * change on the backend is caught when the fixture is refreshed from it.
 *
 * A hidden badge (ENG-411) gets an `icon` at most and never `titleKey` /
 * `descriptionKey`: those messages ship in `en-US/common.json` to every
 * client, so naming a secret badge here would put its name and what earns it
 * in the bundle of listeners who have not found it. Its copy stays the
 * backend's words, which only reach a client the backend serves the entry to.
 */
/**
 * One version-1 rung of a ladder as `[key, presentation]`: the backend key
 * `<metric>.<name>`, its icon, the threshold its copy describes, and the
 * catalog messages under `achievements.badges.<metric>.<name>`.
 */
const rung = (metric, name, icon, threshold) => {
  const key = `${metric}.${name}`;
  return [
    key,
    Object.freeze({
      icon,
      versions: Object.freeze({ 1: threshold }),
      titleKey: `achievements.badges.${key}.title`,
      descriptionKey: `achievements.badges.${key}.description`,
    }),
  ];
};

export const ACHIEVEMENT_PRESENTATION = Object.freeze({
  'listen.first': Object.freeze({
    icon: 'headphones',
    versions: Object.freeze({ 1: 1 }),
    titleKey: 'achievements.badges.listen.first.title',
    descriptionKey: 'achievements.badges.listen.first.description',
  }),
  'listen.regular': Object.freeze({
    icon: 'music',
    versions: Object.freeze({ 1: 10 }),
    titleKey: 'achievements.badges.listen.regular.title',
    descriptionKey: 'achievements.badges.listen.regular.description',
  }),
  'listen.devoted': Object.freeze({
    icon: 'star',
    versions: Object.freeze({ 1: 50 }),
    titleKey: 'achievements.badges.listen.devoted.title',
    descriptionKey: 'achievements.badges.listen.devoted.description',
  }),
  'follow.first': Object.freeze({
    icon: 'heart',
    versions: Object.freeze({ 1: 1 }),
    titleKey: 'achievements.badges.follow.first.title',
    descriptionKey: 'achievements.badges.follow.first.description',
  }),
  'follow.five': Object.freeze({
    icon: 'users',
    versions: Object.freeze({ 1: 5 }),
    titleKey: 'achievements.badges.follow.five.title',
    descriptionKey: 'achievements.badges.follow.five.description',
  }),
  'follow.scene': Object.freeze({
    icon: 'compass',
    versions: Object.freeze({ 1: 25 }),
    titleKey: 'achievements.badges.follow.scene.title',
    descriptionKey: 'achievements.badges.follow.scene.description',
  }),
  'request.first': Object.freeze({
    icon: 'play',
    versions: Object.freeze({ 1: 1 }),
    titleKey: 'achievements.badges.request.first.title',
    descriptionKey: 'achievements.badges.request.first.description',
  }),
  'request.ten': Object.freeze({
    icon: 'trophy',
    versions: Object.freeze({ 1: 10 }),
    titleKey: 'achievements.badges.request.ten.title',
    descriptionKey: 'achievements.badges.request.ten.description',
  }),
  'request.fifty': Object.freeze({
    icon: 'crown',
    versions: Object.freeze({ 1: 50 }),
    titleKey: 'achievements.badges.request.fifty.title',
    descriptionKey: 'achievements.badges.request.fifty.description',
  }),
  // The ladders that arrived after the first nine, with the backend's own
  // icon names. Built by `rung` because a quoted `tip.<name>` would read
  // as a catalog key in the `tip` namespace to the catalog gate.
  ...Object.fromEntries([
    rung('streak', 'three', 'flame', 3),
    rung('streak', 'week', 'calendar-week', 7),
    rung('streak', 'month', 'calendar-month', 30),
    rung('request.played', 'first', 'speaker', 1),
    rung('request.played', 'five', 'applause', 5),
    rung('request.played', 'twentyfive', 'stage', 25),
    // Paid requests and boosts (ENG-824, ENG-826): thresholds in cents.
    rung('request.paid', 'first', 'ticket', 1),
    rung('request.paid', 'twentyfive', 'spotlight', 2500),
    rung('request.paid', 'hundred', 'crown', 10000),
    rung('boost', 'first', 'rocket', 1),
    rung('boost', 'twentyfive', 'lightning', 2500),
    rung('boost', 'hundred', 'megaphone', 10000),
    rung('tip', 'first', 'coin', 1),
    rung('tip', 'twentyfive', 'gift', 2500),
    rung('tip', 'hundred', 'gem', 10000),
  ]),
});

const METRIC_ICONS = Object.freeze({
  listen: 'headphones',
  streak: 'flame',
  follow: 'heart',
  request: 'music',
  'request.played': 'speaker',
  'request.paid': 'ticket',
  boost: 'rocket',
  tip: 'coin',
});
const DEFAULT_ICON = 'trophy';

const asArray = (value) => (Array.isArray(value) ? value : []);

const toDate = (value) => {
  if (value == null || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** A non-negative whole number, or `fallback` when the backend sent nothing usable. */
const toCount = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : fallback;
};

/**
 * The badge definition version an entry carries: a positive whole number as
 * sent, else `null`. The contract always sends one, so an entry without it
 * (or with one the client cannot read) is not assumed to be the first
 * version: `null` matches no catalog message and the backend's words are
 * shown. (Unlike ENG-382's `schemaVersion`, where the backend defined absent
 * as 1 for rows written before the field existed, nothing defines that here.)
 */
const toVersion = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 ? number : null;
};

const isStatus = (value) => Object.values(ACHIEVEMENTS_STATUS).includes(value);

/**
 * Metric of a backend key when the entry did not say: the part before the
 * last dot, since a key is `<metric>.<name>` and a metric can itself hold a
 * dot (`request.first` → `request`, `request.paid.first` → `request.paid`).
 */
const metricOfKey = (key) => {
  const dot = key.lastIndexOf('.');
  return dot > 0 ? key.slice(0, dot) : null;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/** A category key as the backend spells it, or `''`; `other` is the client's own catch-all and never a served key. */
const toCategoryKey = (value) => {
  const key = typeof value === 'string' ? value.trim() : '';
  return key === OTHER_CATEGORY.id ? '' : key;
};

/**
 * The groups the page shows, in order, as `{ id, labelKey, title }`: the
 * response's `categories` as served (deduplicated, entries without a key
 * dropped), then any category this build knows that the response did not
 * list (an entry can still fall back to one by its metric), then
 * `OTHER_CATEGORY`. `labelKey` is the catalog key of a heading this build
 * has (`ACHIEVEMENT_CATEGORIES`), else `null`; `title` is the backend's
 * English heading, shown when there is no `labelKey`, or `null`.
 */
const normalizeCategories = (served) => {
  const categories = [];
  const ids = new Set();
  for (const entry of asArray(served)) {
    const id = toCategoryKey(entry?.key);
    if (!id || ids.has(id)) continue;
    ids.add(id);
    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title.trim() : id;
    categories.push({ id, labelKey: ACHIEVEMENT_CATEGORIES[id]?.labelKey ?? null, title });
  }
  for (const [id, { labelKey }] of Object.entries(ACHIEVEMENT_CATEGORIES)) {
    if (!ids.has(id)) categories.push({ id, labelKey, title: null });
  }
  categories.push({ id: OTHER_CATEGORY.id, labelKey: OTHER_CATEGORY.labelKey, title: null });
  return categories;
};

/**
 * The group an entry is shown under: the `category` it names when the page
 * has that group (served in `categories`, or one this build knows), else the
 * one its metric belongs to, else `OTHER_CATEGORY`.
 */
const categoryOf = (entry, metric, groupIds) => {
  const named = toCategoryKey(entry.category);
  if (named && groupIds.has(named)) return named;
  return (metric && hasOwn(METRIC_CATEGORIES, metric) ? METRIC_CATEGORIES[metric] : null) ?? OTHER_CATEGORY.id;
};

/**
 * The icon name an entry is shown with: this build's own choice for the key
 * (`ACHIEVEMENT_PRESENTATION`), else the served `icon` when the page has a
 * glyph for it, else the metric's, else a generic trophy. A name the backend
 * proposes that this build does not know never reaches the page.
 */
const iconOf = (entry, key, metric) => {
  const served = typeof entry.icon === 'string' ? entry.icon.trim() : '';
  return (
    ACHIEVEMENT_PRESENTATION[key]?.icon ??
    (KNOWN_ICONS.has(served) ? served : null) ??
    (metric && hasOwn(METRIC_ICONS, metric) ? METRIC_ICONS[metric] : null) ??
    DEFAULT_ICON
  );
};

/**
 * One backend achievement entry as the page shows it, or `null` for an entry
 * with no key (nothing to identify it by, in the seen set or the DOM).
 *
 * `progress` is capped at the target for display and the progress bar; a
 * locked badge never rounds up to 100%. `unlocked` follows the backend's flag
 * (or `state`, if the flag is missing), never the numbers: a follow badge can
 * sit below its threshold while unlocked, and a lowered threshold is unlocked
 * by the backend on read, not by the client.
 *
 * `title` and `description` are the backend's English words (the title falls
 * back to the key, so a card always has a heading); `describeAchievement`
 * decides at render time whether the locale's catalog message or these are
 * shown, by `key`, `version` and `target`.
 *
 * `hidden` is the backend's flag as sent, in either casing (`hidden`,
 * `is_hidden`): a badge the listener is not told about until they earn it. It
 * is kept on the unlocked entry so the page can say the badge was a secret;
 * while locked the entry never leaves `normalizeAchievementsResponse`.
 */
const normalizeAchievement = (entry, groupIds) => {
  if (!entry || typeof entry !== 'object') return null;
  const key = typeof entry.key === 'string' ? entry.key.trim() : '';
  if (!key) return null;

  const metric = typeof entry.metric === 'string' && entry.metric ? entry.metric : metricOfKey(key);
  const version = toVersion(entry.version);
  const target = Math.max(1, toCount(entry.threshold, 1));
  const rawProgress = toCount(entry.progress);
  const unlocked = typeof entry.unlocked === 'boolean' ? entry.unlocked : entry.state === ACHIEVEMENT_STATE.UNLOCKED;
  const state = unlocked
    ? ACHIEVEMENT_STATE.UNLOCKED
    : Object.values(ACHIEVEMENT_STATE).includes(entry.state)
      ? entry.state
      : rawProgress > 0
        ? ACHIEVEMENT_STATE.IN_PROGRESS
        : ACHIEVEMENT_STATE.LOCKED;
  const progress = Math.min(rawProgress, target);

  return {
    key,
    version,
    metric,
    category: categoryOf(entry, metric, groupIds),
    icon: iconOf(entry, key, metric),
    title: typeof entry.title === 'string' && entry.title.trim() ? entry.title.trim() : key,
    description: typeof entry.description === 'string' ? entry.description.trim() : '',
    unit: typeof entry.unit === 'string' && entry.unit.trim() ? entry.unit.trim() : ACHIEVEMENT_UNIT.COUNT,
    target,
    progress,
    percent: unlocked ? 100 : Math.min(99, Math.floor((progress / target) * 100)),
    state,
    unlocked,
    unavailable: state === ACHIEVEMENT_STATE.UNAVAILABLE,
    hidden: (entry.hidden ?? entry.is_hidden) === true,
    unlockedAt: unlocked ? toDate(entry.unlockedAt ?? entry.unlocked_at) : null,
  };
};

/**
 * Whether a normalized badge may be shown at all. A hidden badge is a
 * surprise: until the backend reports it unlocked, the listener is told
 * nothing of it — showing a masked card would still say that something is
 * there to find. Every other badge is always shown, locked or not.
 */
export const isRevealedAchievement = (achievement) => !achievement.hidden || achievement.unlocked;

/**
 * The `GET /me/achievements` payload as the page reads it. Tolerant of a
 * response missing everything but `achievements`: `status` defaults to
 * `ready`, and the per-source map and `asOf` to nothing. Entries without a
 * key are dropped; a duplicate key keeps its first entry, so the DOM and the
 * seen set never hold a badge twice. A hidden badge that is not unlocked yet
 * is dropped too (`isRevealedAchievement`), so nothing downstream — the
 * counts, the groups, "next up", the seen set — can give it away.
 *
 * `withheldCount` is how many entries that dropped, and is the one thing the
 * page learns about them: the catalogue it was handed is not the whole one,
 * so it must not tell the listener they have collected every badge there is
 * (`summarizeAchievements`). A bare count names no badge, says nothing of
 * what earns one and is never rendered — it only turns a claim off.
 */
export const normalizeAchievementsResponse = (payload) => {
  const body = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const categories = normalizeCategories(body.categories);
  const groupIds = new Set(categories.map((category) => category.id));
  const seenKeys = new Set();
  const achievements = [];
  let withheldCount = 0;
  for (const entry of asArray(body.achievements)) {
    const achievement = normalizeAchievement(entry, groupIds);
    if (!achievement || seenKeys.has(achievement.key)) continue;
    // Claimed by its first entry whether or not that one is shown, so a
    // second entry for the same key cannot reveal what the first withheld.
    seenKeys.add(achievement.key);
    if (!isRevealedAchievement(achievement)) {
      withheldCount += 1;
      continue;
    }
    achievements.push(achievement);
  }

  const sources = {};
  if (body.sources && typeof body.sources === 'object') {
    for (const [metric, status] of Object.entries(body.sources)) {
      if (isStatus(status)) sources[metric] = status;
    }
  }

  const sourceStatuses = Object.values(sources);
  const status = isStatus(body.status)
    ? body.status
    : sourceStatuses.includes(ACHIEVEMENTS_STATUS.PROCESSING)
      ? ACHIEVEMENTS_STATUS.PROCESSING
      : sourceStatuses.includes(ACHIEVEMENTS_STATUS.UNAVAILABLE)
        ? ACHIEVEMENTS_STATUS.UNAVAILABLE
        : ACHIEVEMENTS_STATUS.READY;

  return {
    status,
    sources,
    catalogVersion: toCount(body.catalogVersion, null),
    asOf: toDate(body.asOf),
    categories,
    achievements,
    withheldCount,
  };
};

/**
 * The badges grouped for the page, in group order (`categories` from
 * `normalizeAchievementsResponse`), leaving out groups with nothing in them.
 * Each group is `{ category, label, source, items }`: `label` is the
 * catalog heading in the locale of `t` when this build has one (`source`
 * `catalog`), else the backend's English title (`source` `backend`, so the
 * page can mark it `lang="en"` like a badge's backend copy).
 */
export const groupAchievements = (achievements, categories, t = englishT) => {
  const list = asArray(achievements);
  return asArray(categories)
    .map((category) => ({
      category: category.id,
      label: category.labelKey ? t(category.labelKey) : category.title,
      source: category.labelKey ? 'catalog' : 'backend',
      items: list.filter((achievement) => achievement.category === category.id),
    }))
    .filter((group) => group.items.length > 0);
};

/**
 * Whether the listener has done anything the badges count yet: some
 * progress on any badge, or an unlock (a follow badge can be unlocked at
 * progress 0 after unfollowing).
 */
export const hasAchievementActivity = (achievements) =>
  asArray(achievements).some((achievement) => achievement.unlocked || achievement.progress > 0);

/**
 * The metrics with a badge the listener can still earn here: locked, and not
 * `unavailable`. Empty when every badge is unlocked, when every remaining one
 * belongs to a source this deployment does not enable, or when there are no
 * badges at all, so the page can stop urging the listener to do things that
 * cannot earn anything.
 */
export const earnableMetrics = (achievements) =>
  new Set(
    asArray(achievements)
      .filter((achievement) => !achievement.unlocked && !achievement.unavailable && achievement.metric)
      .map((achievement) => achievement.metric)
  );

/**
 * Headline numbers for the page: how many badges are unlocked and which locked
 * badge is closest (highest percent; the smaller target breaks ties so the
 * suggestion is the one the listener can actually finish next). A badge whose
 * source is unavailable cannot be finished, so it is never next up.
 *
 * `allCollected` is whether the listener has finished the set, and the only
 * thing that may say so: with badges unlocked and nothing left it is true,
 * but `withheldCount` (the hidden badges the response carried and this
 * catalogue does not, ENG-411) makes it false, because there is a badge left
 * to earn — the listener is simply not being told about it. Congratulating
 * them on a complete set and then growing the total when the secret unlocks
 * would read as a bug; saying nothing reveals nothing.
 */
export const summarizeAchievements = (achievements, withheldCount = 0) => {
  const list = asArray(achievements);
  const unlockedCount = list.filter((achievement) => achievement.unlocked).length;
  const nextUp =
    list
      .filter((achievement) => !achievement.unlocked && !achievement.unavailable)
      .sort((a, b) => b.percent - a.percent || a.target - b.target)[0] ?? null;
  return {
    unlockedCount,
    total: list.length,
    nextUp,
    allCollected: list.length > 0 && unlockedCount === list.length && withheldCount === 0,
  };
};

/**
 * How progress reads on a card, by the entry's `unit`: `3 / 10` for a count,
 * `$2.50 / $25.00` for cents (US dollars in the notation of `format`'s
 * locale, always with the cents so the two amounts line up). Any other unit
 * is shown as the bare numbers. In the locale of `t` and `format`; English
 * without them.
 */
export const formatProgress = (achievement, t = englishT, format = englishFormat) => {
  const amount = (value) =>
    achievement.unit === ACHIEVEMENT_UNIT.CENTS ? format.usd(value / 100, { cents: 'always' }) : value;
  return t('achievements.badge.progress', { progress: amount(achievement.progress), target: amount(achievement.target) });
};

/** Whether the active locale itself has the message (a translator without `has`, a plain function, is trusted to). */
const hasTranslation = (t, key) => typeof t.has !== 'function' || t.has(key);

/**
 * A badge's title and description in the given locale (English without a
 * `t`), as `{ title, description, source }`:
 *   catalog  the `achievements.badges.*` messages, when the client knows the
 *            backend `key` and the entry's `version` (`ACHIEVEMENT_PRESENTATION`),
 *            the served threshold (`target`) is the one that version's copy
 *            describes, and the active locale has both messages — the two
 *            are one badge's copy and are localized together or not at all
 *   backend  the words the backend sent, otherwise: a badge this build does
 *            not know, a definition version it does not know (whose words
 *            may say something else), a known version served with another
 *            threshold (the catalog would contradict the progress bar), or a
 *            locale without the translation. Never a guess.
 * A presentation entry without copy (`titleKey`, `descriptionKey`,
 * `versions`) is an icon-only entry and reads as unknown copy.
 */
export const describeAchievement = (achievement, t = englishT) => {
  const backend = { title: achievement.title, description: achievement.description, source: 'backend' };
  const spec = ACHIEVEMENT_PRESENTATION[achievement.key];
  if (!spec?.titleKey || !spec.descriptionKey || achievement.version == null) return backend;
  if (spec.versions?.[achievement.version] !== achievement.target) return backend;
  if (!hasTranslation(t, spec.titleKey) || !hasTranslation(t, spec.descriptionKey)) return backend;
  return { title: t(spec.titleKey), description: t(spec.descriptionKey), source: 'catalog' };
};

/**
 * The normalized badges with `title` and `description` as the page shows
 * them in the locale of `t` (see `describeAchievement`), so the cards, the
 * progress-bar labels and the "next up" line all say the same thing. Each
 * carries `source` (`catalog` | `backend`) too: the backend's words are
 * English whatever the locale, so the page can mark them `lang="en"` for
 * assistive technology when the active language is not English.
 */
export const localizeAchievements = (achievements, t = englishT) =>
  asArray(achievements).map((achievement) => ({ ...achievement, ...describeAchievement(achievement, t) }));

// ── "New" tracking ────────────────────────────────────────────────────────
// Keys of the unlocked badges the listener has already seen on the
// achievements page (per device — it lives in localStorage), so a badge
// earned since the last visit can be called out once.

/**
 * The ids the seen set held before ENG-371 re-keyed it. They identify no
 * badge any more, so a set made only of them says nothing about what this
 * device has seen and is read as no set at all: the first-visit rule applies
 * and the listener is not greeted by a wall of "New" chips. Listed by name
 * rather than by shape so a backend key of any shape is never mistaken for
 * one of them.
 */
const PRE_ENG_371_SEEN_IDS = new Set([
  'first-request',
  'requests-10',
  'requests-50',
  'first-played',
  'played-25',
  'first-tip',
  'tips-total-50',
  'tips-total-250',
  'bands-3',
  'bands-10',
  'active-days-7',
  'active-days-30',
]);

/**
 * Between ENG-371 and ENG-370 the badges the backend did not serve were keyed
 * `local:<what the client measured>.<name>`, so nothing the client stored
 * could be mistaken for a backend key. Those badges are gone with the
 * cutover, so the keys identify nothing and are dropped like the pre-ENG-371
 * ids. A backend key never carries this prefix.
 */
export const LOCAL_KEY_PREFIX = 'local:';

export const isLocalAchievementKey = (key) => typeof key === 'string' && key.startsWith(LOCAL_KEY_PREFIX);

const isRetiredSeenKey = (key) => PRE_ENG_371_SEEN_IDS.has(key) || isLocalAchievementKey(key);

/**
 * The stored seen set, or `null` when nothing usable has been stored on this
 * device. Retired keys (pre-ENG-371 ids, `local:` keys) are dropped on the
 * way in, so the next write no longer carries them; a set made only of them
 * reads as a first visit.
 */
export const readSeenAchievementKeys = (storage = globalThis.localStorage) => {
  try {
    const raw = storage.getItem(ACHIEVEMENTS_SEEN_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const keys = parsed.filter((key) => typeof key === 'string' && !isRetiredSeenKey(key));
    return keys.length === 0 && parsed.length > 0 ? null : new Set(keys);
  } catch {
    return null;
  }
};

export const writeSeenAchievementKeys = (keys, storage = globalThis.localStorage) => {
  try {
    storage.setItem(ACHIEVEMENTS_SEEN_KEY, JSON.stringify([...new Set(keys)]));
  } catch {
    // Storage unavailable — the first-visit rule applies again next time.
  }
};

/**
 * Which unlocked badges to flag "New", by key. With a seen set, anything not
 * in it; on a first visit, only badges unlocked in the last 24h (same rule
 * the /home feed uses) so a long-time listener is not greeted by a wall of
 * chips.
 */
export const pickNewAchievementKeys = (achievements, seen, now = new Date()) =>
  new Set(
    asArray(achievements)
      .filter((achievement) => achievement.unlocked)
      .filter((achievement) =>
        seen instanceof Set
          ? !seen.has(achievement.key)
          : achievement.unlockedAt instanceof Date &&
            now.getTime() - achievement.unlockedAt.getTime() <= NEW_ACHIEVEMENT_WINDOW_MS
      )
      .map((achievement) => achievement.key)
  );
